import { configureStore } from "@reduxjs/toolkit";
import { thunk } from "redux-thunk";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import loaderReducer from "./Slices/loaderSlice";
import toastReducer from "./Slices/toastSlice";
import authReducer from "./Slices/authSlice";
import userReducer from "./Slices/userSlice";
import teamsReducer from "./Slices/teamsSlice";
import integrationsReducer from "./Slices/integrationSlice";
import projectReducer from "./Slices/Project/projectSlice";
import domainReducer from "./Slices/Project/customDomainSlice";
import redirectRewriteReducer from "./Slices/Project/redirectRewriteSlice";
import headerReducer from "./Slices/Project/headerSlice";
import eventsReducer from "./Slices/Project/eventSlice";
import monitoringReducer from "./Slices/monitoringSlice";
import analyticsReducer from "./Slices/analyticsSlice";
import orgReducer from "./Slices/organizationSlice";
import pandaEdgeReducer from "./Slices/pandaEdgeSlice";
import configurationReducer from "./Slices/configurationSlice";
import ssoReducer from "./Slices/ssoSlice";
import managedAppsReducer from "./Slices/managedAppsSlice";
import aiReducer from "./Slices/aiSlice";
import activityLogsReducer from "./Slices/activityLogsSlice";
import planReducer from "./Slices/planSlice";
import databaseReducer from "./Slices/databaseSlice";
import dashboardReducer from "./Slices/dashboardSlice";
import apiTokensReducer from "./Slices/Settings/apiTokenSlice";
import deleteOrgReducer from "./Slices/Settings/deleteOrgSlice";
import notificationSettingsReducer from "./Slices/Settings/notificationSettingsSlice";
import passwordReducer from "./Slices/Settings/passwordSlice";
import edgeFunctionsReducer from "./Slices/edgeFunctionsSlice";
import edgeAuthReducer from "./Slices/edgeAuthSlice";
import managedAppCustomDomainReducer from "./Slices/ManagedApps/managedAppCustomDomainSlice";

// Configuration for root persistence
const persistConfig = {
  key: "root",
  storage,
  blacklist: [
    "analytics",
    "project",
    "dashboard",
    "loader",
    "toast",
    "monitoring",
    "events",
    "headers",
    "redirectRewrite",
    "customDomain",
    "integrations",
    "teams",
    "managedApps",
    "ai",
    "activityLogs",
    "configurations",
    "apiTokens",
    "deleteOrg",
    "notificationSettings",
    "password",
    "edgeFunctions",
    "edgeAuth",
    "managedAppCustomDomain",
  ],
};

// Configuration for user-specific persistence
const userConfig = {
  key: "user",
  storage,
  whitelist: ["data", "userRole"],
  // Ensure clean state on errors
  serialize: (data) => {
    try {
      return JSON.stringify(data);
    } catch (err) {
      console.error("Error serializing user state:", err);
      return JSON.stringify({});
    }
  },
};

// Configuration for org-specific persistence
const orgConfig = {
  key: "org",
  storage,
  whitelist: ["active", "activePlan"],
  // Ensure clean state on errors
  serialize: (data) => {
    try {
      return JSON.stringify(data);
    } catch (err) {
      console.error("Error serializing org state:", err);
      return JSON.stringify({});
    }
  },
};

const orgPersistedReducer = persistReducer(orgConfig, orgReducer);
const userPersistedReducer = persistReducer(userConfig, userReducer);

const store = configureStore({
  reducer: {
    loader: loaderReducer,
    toast: toastReducer,
    auth: authReducer,
    user: userPersistedReducer,
    teams: teamsReducer,
    integrations: integrationsReducer,
    project: projectReducer,
    customDomain: domainReducer,
    redirectRewrite: redirectRewriteReducer,
    headers: headerReducer,
    events: eventsReducer,
    monitoring: monitoringReducer,
    analytics: analyticsReducer,
    org: orgPersistedReducer,
    configuration: configurationReducer,
    pandaEdge: pandaEdgeReducer,
    sso: ssoReducer,
    managedApps: managedAppsReducer,
    ai: aiReducer,
    activityLogs: activityLogsReducer,
    plans: planReducer,
    configurations: databaseReducer,
    dashboard: dashboardReducer,
    apiTokens: apiTokensReducer,
    deleteOrg: deleteOrgReducer,
    notificationSettings: notificationSettingsReducer,
    password: passwordReducer,
    edgeFunctions: edgeFunctionsReducer,
    edgeAuth: edgeAuthReducer,
    managedAppCustomDomain: managedAppCustomDomainReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: [
          "persist/PERSIST",
          "persist/REHYDRATE",
          "persist/PURGE",
        ],
        // Ignore these field paths in all actions
        ignoredActionPaths: ["payload.timestamp", "meta.arg"],
        // Ignore these paths in the state
        ignoredPaths: ["items.dates"],
      },
      immutableCheck: false, // Disable for performance in production
    }).concat(thunk),
});

export const persistor = persistStore(store);

export default store;
